import { ReactNode } from 'react';
import { LinkIcon } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

// Datasheet-style spec card primitives shared by JobDetailsCard / JobSettingsCard.

// Shorten a URL to its bare hostname (fallback: the raw string).
export const hostOf = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

export function SpecCard({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
}) {
  return (
    <Card className="h-full overflow-hidden">
      <div className="flex items-center gap-3 border-b px-5 py-4">
        <span className="grid h-9 w-9 place-items-center rounded-lg bg-primary/10 text-primary">
          {icon}
        </span>
        <span className="text-[13px] font-bold uppercase tracking-[0.08em]">
          {title}
        </span>
      </div>
      <div className="px-5 pb-4 pt-1">{children}</div>
    </Card>
  );
}

export function SpecCaption({ children }: { children: ReactNode }) {
  return (
    <p className="mb-1.5 mt-3.5 text-[10.5px] font-bold uppercase tracking-[0.09em] text-muted-foreground/70">
      {children}
    </p>
  );
}

export function SpecRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-dashed py-1.5 last:border-b-0">
      <span className="shrink-0 text-[13px] text-muted-foreground">
        {label}
      </span>
      <span className="text-right text-[13.5px] font-semibold">{children}</span>
    </div>
  );
}

export function SpecPill({
  active = false,
  children,
}: {
  active?: boolean;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
        active ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
      )}
    >
      {children}
    </span>
  );
}

export function SpecLinks({ children }: { children: ReactNode }) {
  return <span className="flex flex-wrap justify-end gap-1.5">{children}</span>;
}

export function SpecLink({
  href,
  icon,
  children,
}: {
  href: string;
  icon?: ReactNode;
  children: ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={href}
      className="inline-flex max-w-[200px] items-center gap-1 truncate rounded-md bg-blue-50 px-2 py-0.5 text-[12.5px] font-semibold text-blue-600 hover:bg-blue-100 dark:bg-blue-950/40 dark:text-blue-400 dark:hover:bg-blue-900/40"
    >
      {icon ?? <LinkIcon className="h-3 w-3 shrink-0" />}
      <span className="truncate">{children}</span>
    </a>
  );
}

export function SpecDims({
  items,
}: {
  items: { label: string; value: string; unit?: string }[];
}) {
  return (
    <div className="mt-2 grid grid-cols-3 gap-2 rounded-[10px] bg-muted/50 p-3">
      {items.map((d) => (
        <div key={d.label} className="text-center">
          <div className="text-[19px] font-bold tracking-tight tabular-nums">
            {d.value}
            {d.unit && (
              <span className="ml-0.5 text-[11px] font-medium text-muted-foreground">
                {d.unit}
              </span>
            )}
          </div>
          <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            {d.label}
          </div>
        </div>
      ))}
    </div>
  );
}
